import { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { HashRouter, Route, Redirect } from 'react-router-dom'
import axios from 'axios'

import { httpService, HTTP_MODE } from '../../common/services/http.service'
import { RouterContext, router } from '../../common/contexts/routerContext'
import { FullPageLoader } from '../../common/components/full-page-loader'
import { NotificationContainer } from '../../common/components/Notifications'
import { errorHandlerService } from '../../common/services/error-handler.service'
import { UserContextProvider } from '../../common/contexts/userContext'
import { REPORT_VIEWER_MODE } from '../constants'
import { Report } from '../report.model'
import 'katex/dist/katex.css'
import '../../../style/app'
import { ReportViewer } from '../viewers/report-viewer'
import { BlockDataRequestInterceptor } from '../../interceptors/request/report-block-data'
import { ReferenceDataRequestInterceptor } from '../../interceptors/request/report-reference-data'
import { RevokeObjectURLResponseInterceptor } from '../../interceptors/response/revoke-object-url'

import { STANDALONE_CONTENT_FOLDER, STANDALONE_DATA_FOLDER, STANDALONE_REFERENCE_DATA } from './constants'
import { CookieBanner } from './cookie-banner'

httpService.initialize(HTTP_MODE.STANDALONE)

ReactDOM.render(
  <StandaloneReportRouter />,
  document.getElementById('app-container')
)

/**
 * Routing for the standalone report
 *
 * This mirrors the report viewing routes from the authenticated app so that links between pages inside the report will
 * function properly without special branching logic in the code.
 */
function StandaloneReportRouter () {
  return (
    <UserContextProvider>
      <RouterContext.Provider value={ router }>
        <div className="standalone-report">
          <div className="app-content body-text full-view-height">
            <HashRouter>
              <NotificationContainer />
              <Route exact path="/reports/:id/view" render={({ match }) => (
                <StandaloneReportViewer id={match.params.id} />
              )} />
              <Route exact path="/" render={() => (
                <StandaloneReportRoot />
              )} />
            </HashRouter>
          </div>
        </div>
      </RouterContext.Provider>
    </UserContextProvider>
  )
}

/**
 * Loads the report from a JSON file and renders the appropriate report viewer
 */
function StandaloneReportViewer ({ id }) {
  const [report, setReport] = useState()

  const requestInterceptors = [
    new BlockDataRequestInterceptor(STANDALONE_DATA_FOLDER),
    new ReferenceDataRequestInterceptor(STANDALONE_REFERENCE_DATA)
  ]

  const responseInterceptors = [
    new RevokeObjectURLResponseInterceptor()
  ]

  useEffect(() => {
    setReport()
    loadReport(id)
  }, [ id ])

  useEffect(() => {
    if (report) {
      const trackerConfig = window._paq ?? []
      trackerConfig.push(['setCustomUrl', location.href])
      trackerConfig.push(['setDocumentTitle', report.title])
      trackerConfig.push(['trackPageView'])
    }
  }, [ report ])

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(onRequest)
    const responseInterceptor = axios.interceptors.response.use(onResponse)

    return () => {
      axios.interceptors.request.eject(requestInterceptor)
      axios.interceptors.response.eject(responseInterceptor)
    }
  })

  async function loadReport (id) {
    const result = await axios.get(`${STANDALONE_CONTENT_FOLDER}/${id}.json`)
    setReport(new Report(result.data))
  }

  async function onRequest (config) {
    for (let interceptor of requestInterceptors) {
      let use = interceptor.use({ config }, { report })
      if (use) {
        return use
      }
    }

    return config
  }

  async function onResponse (response) {
    for (let interceptor of responseInterceptors) {
      let use = interceptor.use(response, { report })
      if (use) {
        return use
      }
    }

    return response
  }

  return report
    ? (
      <>
        <ReportViewer report={report} mode={REPORT_VIEWER_MODE.READ} />
        <CookieBanner />
      </>
    )
    : <FullPageLoader />
}

/**
 * Loads the main report id from a report.json file and then redirect the appropriate route to render the report viewer
 */
function StandaloneReportRoot () {
  const [reportId, setReportId] = useState()

  useEffect(() => {
    getReportId()
  }, [])

  async function getReportId () {
    try {
      const report = await axios.get(`reportcontent/report.json`)
      setReportId(report.data.id)
    } catch (error) {
      const message = 'Unable to find the report content.'
      errorHandlerService.handleServerError(error, message)
    }
  }

  if (!reportId) {
    return <FullPageLoader />
  }

  return <Redirect to={{ pathname: `/reports/${reportId}/view` }} />
}
